import { ResizeBorder } from '@/pages/LessonEditor/components/ResizeBorder';
import { Coordinate, isSlideComponentText, PropsWithPreview, Size, SlideComponent } from '@/types/slide';
import DeleteIcon from '@mui/icons-material/Delete';
import TitleIcon from '@mui/icons-material/Title';
import { Box } from '@mui/material';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ComponentLabel } from '../ComponentLabel';
import { ContextualBar } from '../ContextualBar';
import { useSlideComponent } from '../hooks/useSlideComponent';
import { TypeSettingsPopover } from './TypeSettingsPopover';

// Import the fonts CSS file
import '@/assets/styles/fonts.css';
import { capture } from '@/handlers/error';
import { useOutsideClick } from '@/hooks/useOutsideClick';
import { useComponentEditor } from '@/providers/editor/EditorProvider';
import { useNotifications } from '@toolpad/core';
import { useDebounce } from 'rooks';
import { useRightClickMenu } from '../hooks/useRightClickMenu';
import { RightClickMenu } from '../RightClickMenu';
import { SlideRef } from '../Slide';
import { TextContent } from './TextContent';

interface Props {
    init: {
        model: SlideComponent;
        focus?: boolean;
    };
    slideRef: React.RefObject<SlideRef | null>;
    ref: React.RefObject<HTMLDivElement | null>;
}

export const SlideComponentText: React.FC<PropsWithPreview<Props>> = ({ ref, init, preview, slideRef }) => {
    const { t } = useTranslation();
    const notifications = useNotifications();
    const { deleteComponent, updateComponent, bringToFront, bringToBack, copyComponent, pasteComponent, undo, redo } = useComponentEditor();

    const { rightClickMenu, handleRightClickMenu, rightClickMenuClose } = useRightClickMenu();

    const contextBarRef = useRef(null);
    // Add refs for the popovers
    const typeSettingsRef = useRef<HTMLDivElement>(null);

    const { isSelected } = useSlideComponent(ref, [typeSettingsRef], !!preview);

    const [model, setModel] = useState<SlideComponent>({ ...init.model });
    const [isFocus, setIsFocus] = useState<boolean>(init.focus ?? false);

    const [tSize, setTSize] = useState<Size>(model.size);
    const [tPos, setTPos] = useState<Coordinate>(model.pos);

    // Add state for font size picker popover
    const [typeSettingsAnchorEl, setTypeSettingsAnchorEl] = useState<HTMLElement | null>(null);

    const onDeleteComponent = React.useCallback(
        async (id: string) => {
            try {
                await deleteComponent(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [deleteComponent, notifications],
    );

    const onUpdateComponent = React.useCallback(
        async (id: string, component: SlideComponent) => {
            try {
                await updateComponent(id, component);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [updateComponent, notifications],
    );

    const onBringToFrontHandler = React.useCallback(
        async (id: string) => {
            try {
                await bringToFront(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [bringToFront, notifications],
    );

    const onBringToBackHandler = React.useCallback(
        async (id: string) => {
            try {
                await bringToBack(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [bringToBack, notifications],
    );

    const onTextChange = React.useCallback(
        (v: string) => {
            if (!isSlideComponentText(model)) {
                return;
            }

            const updatedContent = { ...model.content, text: v };
            setModel({ ...model, content: updatedContent });
            onUpdateComponent?.(model.id, { ...model, content: updatedContent });
        },
        [model, onUpdateComponent],
    );
    const debouncedTextChange = useDebounce(onTextChange, 500);

    const isEditing = isFocus || isSelected;

    const debouncedUpdateComponent = useDebounce(onUpdateComponent, 500);

    useOutsideClick(ref, () => {
        if (preview) return;
        if (!ref) return;

        setIsFocus(false);
    });

    if (!isSlideComponentText(model)) {
        return null;
    }

    /**
     * make sure to match the component for editing
     */

    return (
        <Box
            onContextMenu={isEditing ? handleRightClickMenu : undefined}
            ref={ref}
            id={`text-component-${model.id}`}
            sx={{
                position: 'absolute',
                left: tPos.x,
                top: tPos.y,
                m: 'inherit',
                p: 'inherit',
                textAlign: 'left',
                width: tSize.width,
                height: tSize.height,
                backgroundColor: model.content.typeSettings?.backgroundColor || 'transparent',
                borderRadius: model.content.typeSettings?.borderRadius ? `${model.content.typeSettings.borderRadius}px` : '0px',
            }}
            onClick={(e) => {
                if (!preview) {
                    e.stopPropagation();
                }
            }}
            onDoubleClick={(e) => {
                if (preview) return;
                e.stopPropagation();
                setTypeSettingsAnchorEl(ref.current);
            }}
        >
            <ResizeBorder
                size={tSize}
                pos={tPos}
                preview={preview}
                isSelected={isEditing}
                onResize={(size, pos) => {
                    setTSize(size);
                    setTPos(pos);
                    const updateModel = { ...model, size, pos };
                    setModel({ ...updateModel });
                    debouncedUpdateComponent?.(model.id, { ...updateModel });
                }}
                slideRef={slideRef}
            >
                {!preview && (
                    <ComponentLabel
                        variant='caption'
                        sx={{
                            ...(!isEditing && {
                                opacity: 0, // Hide by default
                                transition: 'opacity 0.2s ease',
                                [`#text-component-${model.id}:hover &`]: {
                                    opacity: 1, // Show when text-component is hovered
                                },
                            }),
                        }}
                        noWrap
                    >
                        {t('slide.asset.text')}
                    </ComponentLabel>
                )}

                <TextContent
                    preview={preview}
                    autoFocus={isEditing}
                    model={model}
                    onUpdate={(v) => {
                        debouncedTextChange?.(v);
                    }}
                />

                {!preview && isEditing && (
                    <ContextualBar
                        startButtons={[
                            {
                                icon: <TitleIcon />,
                                tooltip: t('slide.components.assets.text.tooltip.typeSettings'),
                                onClick: () => {
                                    setTypeSettingsAnchorEl(contextBarRef.current);
                                },
                            },
                        ]}
                        endButtons={[
                            {
                                icon: <DeleteIcon />,
                                tooltip: t('slide.components.assets.text.tooltip.remove'),
                                onClick: () => {
                                    onDeleteComponent?.(model.id);
                                },
                            },
                        ]}
                        ref={contextBarRef}
                    />
                )}

                {typeSettingsAnchorEl && (
                    <TypeSettingsPopover
                        options={{ borderRadius: true }}
                        anchorEl={typeSettingsAnchorEl}
                        slotProps={{
                            paper: {
                                ref: typeSettingsRef, // Attach the ref to the Paper element
                                sx: {
                                    p: 2,
                                },
                            },
                        }}
                        value={{
                            isPhonetic: model?.content?.typeSettings?.isPhonetic,
                            fontFamily: model?.content?.typeSettings?.fontFamily,
                            fontSize: model?.content?.fontSize,
                            formatting: model?.content?.typeSettings?.formatting,
                            alignment: model?.content?.typeSettings?.alignment,
                            color: model.content.color,
                            borderRadius: model?.content?.typeSettings.borderRadius,
                            backgroundColor: model?.content?.typeSettings.backgroundColor,
                        }}
                        onClose={() => {
                            setTypeSettingsAnchorEl(null);
                        }}
                        onUpdate={(settings) => {
                            const typeSettingsContent = {
                                isPhonetic: settings.isPhonetic,
                                fontFamily: settings.fontFamily,
                                alignment: settings.alignment,
                                formatting: settings.formatting,
                                borderRadius: settings.borderRadius,
                                backgroundColor: settings.backgroundColor,
                            };

                            const updatedContent = { ...model.content, fontSize: settings.fontSize, typeSettings: typeSettingsContent, color: settings.color };
                            setModel({ ...model, content: updatedContent });
                            debouncedUpdateComponent?.(model.id, { ...model, content: updatedContent });
                        }}
                    />
                )}
            </ResizeBorder>

            <RightClickMenu
                componentId={model.id}
                contextMenu={rightClickMenu}
                handleClose={rightClickMenuClose}
                bringToFront={onBringToFrontHandler}
                bringToBack={onBringToBackHandler}
                copyComponent={copyComponent}
                pasteComponent={pasteComponent}
                undo={undo}
                redo={redo}
            />
        </Box>
    );
};
